// components/socials/profile/Profile.jsx
import { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiMoreHorizontal,
} from "react-icons/fi";

import { db } from "../../../firebase/firebase.utils";
import Peep from "../../avatar-creator/Peep";
import Feed from "../Feed";
import ProfilePostsSection from "./ProfilePostsSection";
import { humanizeTagline } from "./profileOptions";
import {
  acceptConnectionRequest,
  blockUser,
  connectionIdFor,
  declineConnectionRequest,
  pruneAuthorScores,
  removeConnection,
  requestIdFor,
  sendConnectionRequest,
  setConnectionMuted,
  toggleFollow,
} from "./profileApi";
import "./profile.styles.scss";

export default function Profile() {
  const { uid: routeUid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.user.currentUser);
  const profileUid = routeUid || currentUser?.uid;
  const isOwnProfile = !!currentUser?.uid && currentUser.uid === profileUid;
  const selectedPostId = searchParams.get("post");

  const [profileUser, setProfileUser] = useState(
    location.state?.user || (isOwnProfile ? currentUser : null),
  );
  const [loading, setLoading] = useState(!profileUser);
  const [slide, setSlide] = useState(0);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [connected, setConnected] = useState(false);
  const [following, setFollowing] = useState(false);
  const [outgoingRequest, setOutgoingRequest] = useState(false);
  const [incomingRequest, setIncomingRequest] = useState(false);

  const connectionId = connectionIdFor(currentUser?.uid, profileUid);
  const outgoingId = requestIdFor(currentUser?.uid, profileUid);
  const incomingId = requestIdFor(profileUid, currentUser?.uid);

  useEffect(() => {
    if (!profileUid) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    return db
      .collection("users")
      .doc(profileUid)
      .onSnapshot(
        (snapshot) => {
          setProfileUser(
            snapshot.exists
              ? { uid: snapshot.id, id: snapshot.id, ...snapshot.data() }
              : null,
          );
          setLoading(false);
        },
        (error) => {
          console.log("Profile load error:", error);
          setProfileUser(null);
          setLoading(false);
        },
      );
  }, [profileUid]);

  useEffect(() => {
    if (!currentUser?.uid || !profileUid || isOwnProfile) {
      setConnected(false);
      setFollowing(false);
      setOutgoingRequest(false);
      setIncomingRequest(false);
      return undefined;
    }

    const subscriptions = [
      db
        .collection("connections")
        .doc(connectionId)
        .onSnapshot(
          (snapshot) => setConnected(snapshot.exists),
          (error) => console.log("Connection listener error:", error),
        ),
      db
        .collection("connectionRequests")
        .doc(outgoingId)
        .onSnapshot(
          (snapshot) =>
            setOutgoingRequest(
              snapshot.exists &&
                (snapshot.data()?.status || "pending") === "pending",
            ),
          (error) => console.log("Outgoing request listener error:", error),
        ),
      db
        .collection("connectionRequests")
        .doc(incomingId)
        .onSnapshot(
          (snapshot) =>
            setIncomingRequest(
              snapshot.exists &&
                (snapshot.data()?.status || "pending") === "pending",
            ),
          (error) => console.log("Incoming request listener error:", error),
        ),
      db
        .collection("follows")
        .doc(`${currentUser.uid}_${profileUid}`)
        .onSnapshot(
          (snapshot) => setFollowing(snapshot.exists),
          (error) => console.log("Follow listener error:", error),
        ),
    ];

    return () => subscriptions.forEach((unsubscribe) => unsubscribe());
  }, [
    currentUser?.uid,
    profileUid,
    isOwnProfile,
    connectionId,
    outgoingId,
    incomingId,
  ]);

  useEffect(() => {
    setSlide(0);
    setAboutOpen(false);
  }, [profileUid]);

  const photos = useMemo(() => {
    const value = profileUser?.imageUrl;
    return (Array.isArray(value) ? value : value ? [value] : []).filter(
      Boolean,
    );
  }, [profileUser?.imageUrl]);

  const canViewPhotos = isOwnProfile || connected;
  const blockedByMe = (currentUser?.blockedUsers || []).includes(profileUid);
  const blockedByThem = (profileUser?.blockedUsers || []).includes(
    currentUser?.uid,
  );
  const unavailable = blockedByMe || blockedByThem;
  const photoSlides = photos.map((url) => ({ type: "photo", url }));
  const prefersAvatar = profileUser?.preferences?.showAvatar !== false;
  const slides =
    prefersAvatar || !photoSlides.length
      ? [{ type: "peep" }, ...photoSlides]
      : [...photoSlides, { type: "peep" }];

  const previousSlide = () => {
    setSlide((current) => (current - 1 + slides.length) % slides.length);
  };

  const nextSlide = () => {
    setSlide((current) => (current + 1) % slides.length);
  };

  const run = async (name, action, successMessage) => {
    if (busy) return;

    try {
      setBusy(name);
      await action();
      if (successMessage) window.alert(successMessage);
    } catch (error) {
      console.log(`${name} error:`, error);
      window.alert("The action could not be completed.");
    } finally {
      setBusy("");
    }
  };

  const onFollow = () => {
    if (unavailable) return window.alert("This profile is unavailable.");

    run("follow", async () => {
      await toggleFollow({
        uid: currentUser.uid,
        profileUid,
        following,
      });
      if (!following) pruneAuthorScores(currentUser.uid).catch(() => {});
    });
  };

  const onConnect = () => {
    if (unavailable) return window.alert("This profile is unavailable.");
    if (outgoingRequest) {
      return window.alert("You already sent a connection request.");
    }
    if (incomingRequest) {
      return window.alert("This person already sent you a request.");
    }

    run(
      "connect",
      () => sendConnectionRequest(currentUser.uid, profileUid),
      "Connection request sent.",
    );
  };

  const onBlock = () => {
    if (
      !window.confirm("Block this user? Your connection will also be removed.")
    ) {
      return;
    }

    run("block", async () => {
      await blockUser(currentUser.uid, profileUid);
      navigate(-1);
    });
  };

  const openProfilePost = (postId) => {
    if (!postId) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("post", postId);
    setSearchParams(nextParams, {
      state: {
        ...(location.state || {}),
        user: profileUser,
        profileFeed: true,
      },
    });
  };

  const closeProfileFeed = () => {
    if (location.state?.profileFeed) {
      navigate(-1);
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("post");
    setSearchParams(nextParams, {
      replace: true,
      state: { user: profileUser },
    });
  };

  if (loading) {
    return (
      <main className="social-route-page social-route-page--center">
        Loading profile…
      </main>
    );
  }

  if (!profileUser) {
    return (
      <main className="social-route-page social-route-page--center">
        <button type="button" onClick={() => navigate(-1)}>
          <FiChevronLeft /> Back
        </button>
        <p>User not found.</p>
      </main>
    );
  }

  const connectLabel = connected
    ? "Connected"
    : outgoingRequest
      ? "Requested"
      : busy === "connect"
        ? "Sending…"
        : "Connect";

  return (
    <main
      className={`social-route-page profile-page ${
        selectedPostId ? "has-profile-feed" : ""
      }`}
    >
      <div className="profile-page__layout">
        <aside className="profile-page__sidebar">
          <section className="profile-hero">
            <div className="profile-hero__slides">
              {slides.map((item, index) => (
                <div
                  className={`profile-hero__slide ${
                    index === slide ? "is-active" : ""
                  }`}
                  key={`${item.type}-${item.url || index}`}
                  aria-hidden={index !== slide}
                >
                  {item.type === "peep" ? (
                    <Peep
                      useravatar={profileUser.imgAvatar}
                      size={280}
                      width={280}
                      height={280}
                      borderRadius={140}
                      borderWidth={0}
                      overflow="visible"
                      backgroundColor="transparent"
                    />
                  ) : (
                    <>
                      <img src={item.url} alt="" />
                      {!canViewPhotos && (
                        <div className="profile-hero__locked" />
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            <header className="profile-hero__topbar">
              <button
                type="button"
                className="icon-button"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <FiChevronLeft />
              </button>

              {isOwnProfile ? (
                <button type="button" onClick={() => navigate("/account")}>
                  Edit profile
                </button>
              ) : (
                <div className="profile-hero__actions">
                  <button
                    type="button"
                    disabled={!!busy || unavailable}
                    onClick={onFollow}
                  >
                    {busy === "follow"
                      ? "…"
                      : following
                        ? "Following"
                        : "Follow"}
                  </button>

                  <button
                    type="button"
                    disabled={
                      !!busy ||
                      outgoingRequest ||
                      incomingRequest ||
                      unavailable
                    }
                    onClick={connected ? undefined : onConnect}
                  >
                    {connectLabel}
                  </button>

                  {connected && (
                    <details className="profile-actions-menu">
                      <summary aria-label="Connection actions">
                        <FiMoreHorizontal />
                      </summary>
                      <div>
                        <button
                          type="button"
                          onClick={() =>
                            run(
                              "mute",
                              () =>
                                setConnectionMuted(
                                  currentUser.uid,
                                  profileUid,
                                  !currentUser?.mutedUsers?.includes(
                                    profileUid,
                                  ),
                                ),
                              currentUser?.mutedUsers?.includes(profileUid)
                                ? "Notifications enabled."
                                : "Notifications muted.",
                            )
                          }
                        >
                          {currentUser?.mutedUsers?.includes(profileUid)
                            ? "Unmute"
                            : "Mute"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm("Remove this connection?")) {
                              run("remove", () =>
                                removeConnection(currentUser.uid, profileUid),
                              );
                            }
                          }}
                        >
                          Remove connection
                        </button>
                        <button
                          type="button"
                          className="danger"
                          onClick={onBlock}
                        >
                          Block
                        </button>
                      </div>
                    </details>
                  )}
                </div>
              )}
            </header>

            {slides.length > 1 && (
              <>
                <button
                  type="button"
                  className="profile-hero__arrow profile-hero__arrow--left"
                  onClick={previousSlide}
                  aria-label="Previous profile image"
                >
                  <FiChevronLeft />
                </button>
                <button
                  type="button"
                  className="profile-hero__arrow profile-hero__arrow--right"
                  onClick={nextSlide}
                  aria-label="Next profile image"
                >
                  <FiChevronRight />
                </button>
              </>
            )}

            <div className="profile-hero__identity">
              <h1>{profileUser.userName || "Unknown"}</h1>
              {!!profileUser.headline && <p>{profileUser.headline}</p>}

              {!!profileUser.selectedTaglineKey && (
                <span>
                  is {humanizeTagline(profileUser.selectedTaglineKey)}
                </span>
              )}

              {!!profileUser.selectedTags?.length && (
                <div className="profile-hero__tags">
                  {profileUser.selectedTags.map((tag) => (
                    <em key={tag}>{tag}</em>
                  ))}
                </div>
              )}

              {slides.length > 1 && (
                <nav aria-label="Profile images">
                  {slides.map((item, index) => (
                    <button
                      type="button"
                      key={`${item.type}-dot-${index}`}
                      className={index === slide ? "active" : ""}
                      onClick={() => setSlide(index)}
                      aria-label={`Show profile image ${index + 1}`}
                      aria-current={index === slide ? "true" : undefined}
                    />
                  ))}
                </nav>
              )}

              {!canViewPhotos && photos.length > 0 && (
                <small>Connect to view photos</small>
              )}
            </div>
          </section>

          {!isOwnProfile && incomingRequest && !connected && (
            <section className="profile-request-card">
              <div>
                <strong>Connection request</strong>
                <p>Wants to connect with you.</p>
              </div>
              <div className="profile-request-card__actions">
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() =>
                    run("accept", () =>
                      acceptConnectionRequest(profileUid, currentUser.uid),
                    )
                  }
                >
                  {busy === "accept" ? "Accepting…" : "Accept"}
                </button>
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() =>
                    run("decline", () =>
                      declineConnectionRequest(profileUid, currentUser.uid),
                    )
                  }
                >
                  {busy === "decline" ? "Declining…" : "Decline"}
                </button>
              </div>
            </section>
          )}

          <section className={`profile-about ${aboutOpen ? "is-open" : ""}`}>
            <h2>About</h2>
            <p>{profileUser.bio?.trim() || "No bio yet."}</p>
            {(profileUser.bio?.length || 0) > 180 && (
              <button
                type="button"
                onClick={() => setAboutOpen((value) => !value)}
              >
                {aboutOpen ? "Show less" : "Show more"} <FiChevronDown />
              </button>
            )}
          </section>
        </aside>

        <section className="profile-page__main">
          {!selectedPostId && (
            <header className="profile-page__toolbar">
              <div>
                <strong>{profileUser.userName || "Unknown"}</strong>
                <span>Profile</span>
              </div>

              <nav aria-label="Profile actions">
                <button
                  type="button"
                  onClick={() => navigate(`/profile/${profileUid}/connections`)}
                >
                  View connections
                </button>

                {isOwnProfile && (
                  <>
                    <button type="button" onClick={() => navigate("/people")}>
                      Find people
                    </button>
                    <button type="button" onClick={() => navigate("/account")}>
                      Edit profile
                    </button>
                  </>
                )}
              </nav>
            </header>
          )}

          <div
            className={`profile-page__posts ${selectedPostId ? "is-feed" : ""}`}
          >
            {selectedPostId ? (
              <div className="profile-page__feed">
                <Feed
                  key={`${profileUid}-${selectedPostId}`}
                  authorId={profileUid}
                  authorOverride={profileUser}
                  initialPostId={selectedPostId}
                  isOwnProfile={isOwnProfile}
                  isConnected={connected}
                  profileViewer
                  onExitProfileViewer={closeProfileFeed}
                />
              </div>
            ) : (
              <div className="profile-page__posts-scroll">
                <ProfilePostsSection
                  profileUserId={profileUid}
                  isOwnProfile={isOwnProfile}
                  isConnected={connected}
                  onOpenPost={openProfilePost}
                />
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
